
import { useEffect, useState, useCallback, useRef } from 'react';
import { useRouter } from '@/lib/router-compat';
import { usePathname } from '@/lib/router-compat';
import { lazy, Suspense } from "react";
import { Outlet } from "@tanstack/react-router";
import { useAuth } from '@/contexts/AuthContext';
import { SyncProvider } from '@/components/providers/SyncProvider';
import { Callout } from '@/components/ui/callout';
import { Users, Handshake, CheckSquare, FolderKanban, LayoutDashboard, Settings, LogOut, Inbox, MessageCircle } from 'lucide-react';
import { DashboardSkeleton } from '@/components/ui/skeletons';
import { AppSidebar } from '@/components/layout/app-sidebar';
import { preloadRoute } from '@/lib/nav-preload';
import { PageTransition } from '@/components/layout/page-transition';
import { MobileTopBar } from '@/components/layout/mobile-top-bar';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';

// Non-critical layout components — deferred to unblock initial render
const CommandPalette = lazy(
  () => import('@/components/ui/command-palette').then(m => ({ default: m.CommandPalette }))
);
const ChatWidget = lazy(
  () => import('@/components/chat/chat-widget').then(m => ({ default: m.ChatWidget }))
);
import { AssistantProvider } from "@/components/assistant/assistant-provider";

const AssistantLauncher = lazy(
  () => import('@/components/assistant/assistant-launcher').then(m => ({ default: m.AssistantLauncher }))
);
const OfflineBanner = lazy(
  () => import('@/components/sync/offline-banner').then(m => ({ default: m.OfflineBanner }))
);

function DashboardLayout() {
  const { token, currentOrg, organizations, isLoading, isSuperAdmin, logout, switchOrganization, loadOrganizations } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const [searchOpen, setSearchOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [optimisticPath, setOptimisticPath] = useState<string | null>(null);
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setOptimisticPath(null);
  }, [pathname]);

  const activePath = optimisticPath ?? pathname;

  // Group sub-routes under a stable section key so the outer PageTransition
  // doesn't remount the nested layout (e.g. settings) on sub-page navigation.
  const sectionKey = pathname?.startsWith('/dashboard/settings')
    ? '/dashboard/settings'
    : pathname;

  useEffect(() => {
    if (!isLoading && !token) {
      router.push('/login');
    }
  }, [token, isLoading, router]);

  useEffect(() => {
    if (token && organizations.length === 0) {
      loadOrganizations().catch(err => {
        console.error('Failed to load organizations:', err);
      });
    }
  }, [token, loadOrganizations, organizations.length]);

  // ⌘K / Ctrl+K global shortcut
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const openSearch = useCallback(() => setSearchOpen(true), []);
  const chatOpenRef = useRef<(() => void) | null>(null);

  if (isLoading || !token) {
    return <div className="flex min-h-screen items-center justify-center p-8"><DashboardSkeleton /></div>;
  }

  return (
    <div className="flex h-dvh overflow-hidden">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:bg-background focus:px-4 focus:py-2 focus:rounded-md focus:border focus:shadow-md"
      >
        Skip to main content
      </a>

      {/* Sidebar — desktop only */}
      <AppSidebar
        currentOrg={currentOrg}
        organizations={organizations}
        onSwitchOrg={switchOrganization}
        onLogout={logout}
        onSearchOpen={openSearch}
        isSuperAdmin={isSuperAdmin}
        // Support chat has no floating bubble; the account dropdown is its
        // entry point on web and native alike.
        onChatOpen={() => chatOpenRef.current?.()}
      />

      {/* Main area — row so the docked assistant can shrink the page content */}
      <div className="flex flex-1 min-w-0 min-h-0 overflow-hidden">
        {currentOrg ? (
          <SyncProvider orgId={currentOrg.id} token={token!}>
            <AssistantProvider>
            <div className="flex flex-col flex-1 min-w-0 min-h-0 overflow-hidden">
              <Suspense fallback={null}><OfflineBanner /></Suspense>
              <MobileTopBar
                orgInitial={currentOrg?.name.charAt(0).toUpperCase()}
                onProfileOpen={() => setProfileOpen(true)}
                onSearchOpen={openSearch}
              />
              {/* Inbox owns its own scroll inside the messages pane. */}
              {pathname?.startsWith('/dashboard/inbox') ? (
                <main ref={mainRef} id="main-content" className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden overflow-x-hidden" style={{ overscrollBehaviorY: 'none' }}>
                  <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
                    <PageTransition transitionKey={sectionKey}>
                      <Outlet />
                    </PageTransition>
                  </div>
                </main>
              ) : (
                <main
                  ref={mainRef}
                  id="main-content"
                  className="flex-1 flex flex-col min-w-0 min-h-0 overflow-x-hidden overflow-y-auto pb-[calc(var(--mobile-bottom-nav-height)+env(safe-area-inset-bottom))] lg:pb-6"
                  style={{ overscrollBehaviorY: 'none' }}
                >
                  <div className="flex-1 flex flex-col min-w-0 min-h-0 p-4 md:p-6">
                    <PageTransition transitionKey={sectionKey}>
                      <Outlet />
                    </PageTransition>
                  </div>
                </main>
              )}
            </div>
            <Suspense fallback={null}><AssistantLauncher /></Suspense>
            <Suspense fallback={null}><CommandPalette open={searchOpen} onOpenChange={setSearchOpen} /></Suspense>
            </AssistantProvider>
          </SyncProvider>
        ) : (
          <div className="flex flex-1 flex-col min-w-0 min-h-0 overflow-hidden">
            <MobileTopBar
              onProfileOpen={() => setProfileOpen(true)}
              onSearchOpen={openSearch}
            />
          <main
            id="main-content"
            className="flex-1 flex flex-col min-w-0 min-h-0 overflow-y-auto overflow-x-hidden pb-[calc(var(--mobile-bottom-nav-height)+env(safe-area-inset-bottom))] lg:pb-6"
          >
            <div className="p-4 md:p-6 flex-1 flex flex-col min-w-0">
              <PageTransition>
                <Callout variant="warning" title="No Organization Selected" className="mb-4">
                  Create or select an organization to start using the CRM.
                </Callout>
                <Outlet />
              </PageTransition>
            </div>
          </main>
          </div>
        )}
      </div>

      {/* Mobile Bottom Tab Bar */}
      <nav
        aria-label="Primary"
        className="app-safe-bottom fixed bottom-0 left-0 right-0 z-50 border-t bg-background lg:hidden"
        style={{ height: 'calc(var(--mobile-bottom-nav-height) + env(safe-area-inset-bottom))' }}
      >
        <div className="flex h-[var(--mobile-bottom-nav-height)] items-center justify-around">
          {([
            { href: '/dashboard', label: 'Home', Icon: LayoutDashboard, active: activePath === '/dashboard' },
            { href: '/dashboard/contacts', label: 'Contacts', Icon: Users, active: activePath === '/dashboard/contacts' },
            { href: '/dashboard/projects', label: 'Projects', Icon: FolderKanban, active: activePath === '/dashboard/projects' || activePath.startsWith('/dashboard/projects/') },
            { href: '/dashboard/deals', label: 'Deals', Icon: Handshake, active: activePath === '/dashboard/deals' },
            { href: '/dashboard/tasks', label: 'Tasks', Icon: CheckSquare, active: activePath === '/dashboard/tasks' || activePath.startsWith('/dashboard/tasks/') },
          ] as const).map(({ href, label, Icon, active }) => (
            <button
              key={href}
              onPointerDown={() => setOptimisticPath(href)}
              onClick={() => router.push(href)}
              onTouchStart={() => preloadRoute(href, router)}
              aria-current={active ? 'page' : undefined}
              aria-label={label}
              className={`flex flex-col items-center justify-center gap-1 py-2 px-4 min-h-[44px] min-w-[44px] text-xs motion-safe:active:scale-95 motion-safe:transition-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md ${
                active ? 'text-primary font-medium' : 'text-muted-foreground'
              }`}
            >
              <Icon className="h-6 w-6" />
              {label}
            </button>
          ))}
        </div>
      </nav>

      <Suspense fallback={null}><ChatWidget source="app" openRef={chatOpenRef} /></Suspense>

      {/* Mobile Profile Sheet */}
      <Sheet open={profileOpen} onOpenChange={setProfileOpen}>
        <SheetContent side="bottom" className="lg:hidden rounded-t-2xl pb-[env(safe-area-inset-bottom)]">
          <SheetHeader className="sr-only">
            <SheetTitle>Profile</SheetTitle>
          </SheetHeader>
          <div className="flex flex-col gap-1 py-2">
            {isSuperAdmin && (
              <button
                onClick={() => { router.push('/dashboard/inbox'); setProfileOpen(false); }}
                className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium hover:bg-accent active:bg-accent/80 transition-colors w-full text-left"
              >
                <Inbox className="h-5 w-5 text-muted-foreground" />
                Inbox
              </button>
            )}
            <button
              onClick={() => { setProfileOpen(false); chatOpenRef.current?.(); }}
              className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium hover:bg-accent active:bg-accent/80 transition-colors w-full text-left"
            >
              <MessageCircle className="h-5 w-5 text-muted-foreground" />
              Support Chat
            </button>
            <button
              onClick={() => { router.push('/dashboard/settings'); setProfileOpen(false); }}
              className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium hover:bg-accent active:bg-accent/80 transition-colors w-full text-left"
            >
              <Settings className="h-5 w-5 text-muted-foreground" />
              Settings
            </button>
            <div className="h-px bg-border my-1" />
            <button
              onClick={() => { setProfileOpen(false); logout(); }}
              className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-destructive hover:bg-destructive/10 active:bg-destructive/20 transition-colors w-full text-left"
            >
              <LogOut className="h-5 w-5" />
              Sign Out
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

import { createFileRoute } from '@tanstack/react-router';
export const Route = createFileRoute('/dashboard')({
  component: DashboardLayout,
  pendingComponent: () => <DashboardSkeleton />,
  pendingMs: 0,
  pendingMinMs: 150,
});

